import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { Esfera, GatilhoObrigacao, ObrigacaoBody, ObrigacaoDetalhe, Periodicidade, Regra, RegimeTributario, RequerEntrega, Variante, VarianteBody } from '@sgc/contracts';
import { del, post, put, get } from '../lib/api';
import { useAuth } from '../lib/auth';
import { OPCOES_REGIME, ROTULO_PERIODICIDADE, fmtData, formatarCompetencia, lerCompetencia } from '../lib/formato';
import { Area, Campo, CampoNumero, Check, Sel, CampoCompetencia } from '../ui/campos';
import { Modal, Pill, Sec } from '../ui/layout';
import { useToast } from '../ui/toast';
import { EditorVariante, resumoVariante } from '../telas/obrigacoes/variante';
import { ChecklistModeloEditor } from '../telas/obrigacoes/checklist';
import { ModalDuplicar } from '../telas/obrigacoes/nova';

export const Route = createFileRoute('/_app/obrigacoes/$id')({ component: ObrigacaoPagina });

const ESFERAS: ReadonlyArray<readonly [Esfera, string]> = [['FEDERAL', 'Federal'], ['ESTADUAL', 'Estadual'], ['MUNICIPAL', 'Municipal'], ['INTERNA', 'Interna']];
const GATILHOS: ReadonlyArray<readonly [GatilhoObrigacao, string]> = [['RECORRENTE', 'Recorrente (por competência)'], ['EVENTO', 'Por evento'], ['ABERTURA', 'Na abertura da empresa'], ['ENCERRAMENTO', 'No encerramento']];
const ENTREGAS: ReadonlyArray<readonly [RequerEntrega, string]> = [['NENHUMA', 'Nenhuma'], ['PROTOCOLO', 'Protocolo / recibo'], ['GUIA', 'Guia (valor e pagamento)'], ['PROTOCOLO_E_GUIA', 'Protocolo e guia']];
const PERIODICIDADES = Object.entries(ROTULO_PERIODICIDADE) as Array<[Periodicidade, string]>;

type Form = ObrigacaoBody & { regra: Regra | null };

function ObrigacaoPagina() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const { eu } = useAuth();
  const papel = eu?.escritorio?.papel;
  const podeEditar = papel === 'ADMIN' || papel === 'SUPERVISOR';
  const q = useQuery({ queryKey: ['obrigacao', id], queryFn: () => get<ObrigacaoDetalhe>(`/v1/obrigacoes/${id}`) });
  const [f, setF] = useState<Form | null>(null);
  useEffect(() => { if (q.data) { const { id: _i, escritorioId: _e, origemId: _o, periodicidade: _p, resumoRegra: _r, prorrogacoes: _pr, ...resto } = q.data; setF(resto); } }, [q.data]);
  const [editVar, setEditVar] = useState<{ vi: number; ii: number | null } | null>(null);
  const [novaVersao, setNovaVersao] = useState<{ inicio: string; fundamento: string } | null>(null);
  const [novaPror, setNovaPror] = useState<{ comp: string; data: string; fundamento: string } | null>(null);
  const [duplicar, setDuplicar] = useState(false);

  const invalidar = () => { void qc.invalidateQueries({ queryKey: ['obrigacao', id] }); void qc.invalidateQueries({ queryKey: ['obrigacoes'] }); };
  const copiar = useMutation({ mutationFn: () => post<ObrigacaoDetalhe>(`/v1/obrigacoes/${id}/copiar`), onSuccess: (c) => { invalidar(); toast.ok('copiada para o escritório — agora pode editar'); void nav({ to: '/obrigacoes/$id', params: { id: c.id } }); }, onError: (e) => toast.erro(e) });
  const salvar = useMutation({
    mutationFn: () => { const { regra, ...ob } = f!; return put<ObrigacaoDetalhe>(`/v1/obrigacoes/${id}`, { obrigacao: ob, regra }); },
    onSuccess: () => { invalidar(); toast.ok('obrigação salva'); }, onError: (e) => toast.erro(e),
  });
  const prorrogar = useMutation({
    mutationFn: () => post(`/v1/obrigacoes/${id}/prorrogacoes`, { competenciaChave: novaPror!.comp ? lerCompetencia(novaPror!.comp) : null, novoVencimento: novaPror!.data, fundamentoLegal: novaPror!.fundamento }),
    onSuccess: () => { invalidar(); setNovaPror(null); toast.ok('prorrogação registrada'); }, onError: (e) => toast.erro(e),
  });
  const removerPror = useMutation({ mutationFn: (pid: string) => del(`/v1/obrigacoes/${id}/prorrogacoes/${pid}`), onSuccess: () => { invalidar(); toast.ok('prorrogação removida'); }, onError: (e) => toast.erro(e) });

  if (!q.data || !f) return <div className="conteudo"><div className="vazio">carregando…</div></div>;
  const o = q.data;
  const global = o.escritorioId === null;
  const editavel = podeEditar && !global;
  const set = (p: Partial<Form>) => setF((x) => ({ ...x!, ...p }));
  const setRegra = (p: Partial<Regra>) => setF((x) => ({ ...x!, regra: { ...(x!.regra ?? { id: '', periodicidade: 'MENSAL', competenciaOrdinaisValidos: [], parcelasMax: 1, versoes: [] }), ...p } }));
  const setVariante = (vi: number, ii: number | null, va: VarianteBody | null) => {
    const versoes = f.regra!.versoes.map((v, i) => {
      if (i !== vi) return v;
      const variantes = [...v.variantes];
      if (va === null) variantes.splice(ii!, 1);
      else if (ii === null) variantes.push({ ...va, id: '', versaoId: v.id } as Variante);
      else variantes[ii] = { ...variantes[ii]!, ...va };
      return { ...v, variantes };
    });
    setRegra({ versoes });
  };

  return (
    <>
      <div className="topbar">
        <div>
          <h1>{o.sigla} <span className="dim" style={{ fontWeight: 500 }}>— {o.nome}</span></h1>
          <div className="sub"><Link to="/obrigacoes">obrigações</Link> › {o.codigo} · {global ? 'catálogo global (somente leitura)' : 'do escritório'}{o.resumoRegra ? ` · ${o.resumoRegra}` : ''}</div>
        </div>
        <div className="acoes">
          <Link className="btn" to="/obrigacoes/simular" search={{ obrigacaoId: o.id }}>Simular</Link>
          {podeEditar && <button className="btn" type="button" onClick={() => setDuplicar(true)} title="cria outra obrigação com a mesma regra, para ajustar">Duplicar</button>}
          {global && podeEditar && <button className="btn primario" type="button" disabled={copiar.isPending} onClick={() => copiar.mutate()}>Copiar para o escritório</button>}
          {editavel && <button className="btn primario" type="button" disabled={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</button>}
        </div>
      </div>
      <div className="conteudo">
        {global && <div className="alerta info">Obrigação do catálogo global. Para mudar dia, ajuste ou fundamento, <b>copie para o escritório</b>: a cópia passa a valer no lugar desta.</div>}

        <Sec num={1} titulo="Identificação">
          <div className="fgrid">
            <Campo className="c2" rotulo="Código" valor={f.codigo} onChange={() => undefined} readOnly />
            <Campo className="c2" rotulo="Sigla" valor={f.sigla} onChange={(v) => set({ sigla: v })} readOnly={!editavel} />
            <Campo className="c8" rotulo="Nome" valor={f.nome} onChange={(v) => set({ nome: v })} readOnly={!editavel} />
            <Sel className="c3" rotulo="Esfera" valor={f.esfera} opcoes={ESFERAS} onChange={(v) => set({ esfera: v as Esfera })} disabled={!editavel} />
            <Campo className="c2" rotulo="Depto. sugerido" valor={f.departamentoSugerido ?? ''} onChange={(v) => set({ departamentoSugerido: v.toUpperCase() || null })} readOnly={!editavel} ajuda="sigla" />
            <Sel className="c3" rotulo="Gatilho" valor={f.gatilho} opcoes={GATILHOS} onChange={(v) => set({ gatilho: v as GatilhoObrigacao })} disabled={!editavel} />
            <Sel className="c4" rotulo="Registro de entrega ao concluir" valor={f.requerEntrega} opcoes={ENTREGAS} onChange={(v) => set({ requerEntrega: v as RequerEntrega })} disabled={!editavel} />
            <Campo className="c4" rotulo="Órgão" valor={f.orgao ?? ''} onChange={(v) => set({ orgao: v || null })} readOnly={!editavel} />
            <Campo className="c6" rotulo="Link oficial" valor={f.linkOficial ?? ''} onChange={(v) => set({ linkOficial: v || null })} readOnly={!editavel} />
            <Check className="c2" rotulo="Exige revisão" valor={f.exigeRevisao} onChange={(v) => set({ exigeRevisao: v })} />
            <Area className="c12" rotulo="Descrição" valor={f.descricao ?? ''} onChange={(v) => set({ descricao: v || null })} linhas={2} />
            <label className="field c10"><span className="rot">Aplica-se aos regimes</span>
              <div className="linha-acoes" style={{ marginTop: 0 }}>
                {OPCOES_REGIME.map(([r, t]) => (
                  <label key={r} className="field check" style={{ minHeight: 30 }}>
                    <input type="checkbox" disabled={!editavel} checked={f.regimesAplicaveis.includes(r)} onChange={(e) => set({ regimesAplicaveis: e.target.checked ? [...f.regimesAplicaveis, r] : f.regimesAplicaveis.filter((x) => x !== r) })} /><span>{t}</span>
                  </label>
                ))}
              </div>
              <span className="ajuda">nenhum marcado = vale para todos</span>
            </label>
            <Check className="c2" rotulo="Ativa" valor={f.ativo} onChange={(v) => set({ ativo: v })} />
          </div>
        </Sec>

        <Sec num={2} titulo="Regra de prazo" sub="versões por vigência (mudança de lei) e variantes por UF/município/regime"
          acoes={editavel && <button className="btn mini" type="button" onClick={() => setNovaVersao({ inicio: formatarCompetencia(Number(`${new Date().getFullYear() + 1}01`)), fundamento: '' })}>+ Nova versão (mudança de lei)</button>}>
          <div className="fgrid">
            <Sel className="c3" rotulo="Periodicidade" valor={f.regra?.periodicidade ?? ''} vazio="— sem regra —" opcoes={PERIODICIDADES} onChange={(v) => setRegra({ periodicidade: v as Periodicidade })} disabled={!editavel} />
            <CampoNumero className="c2" rotulo="Máx. de quotas" valor={f.regra?.parcelasMax ?? 1} onChange={(v) => setRegra({ parcelasMax: Number(v) || 1 })} />
            <Campo className="c3" rotulo="Ordinais válidos" valor={(f.regra?.competenciaOrdinaisValidos ?? []).join(',')} onChange={(v) => setRegra({ competenciaOrdinaisValidos: v.split(',').map((x) => Number(x.trim())).filter((n) => n >= 1) })} readOnly={!editavel} ajuda="ex.: 1 = só o 1º período do ano; vazio = todos" />
          </div>
          {(f.regra?.versoes ?? []).map((v, vi) => (
            <div className="sec-sub" key={v.id || vi}>
              <div className="sec-sub-head">
                vigência a partir de <span className="mono">{formatarCompetencia(v.vigenciaCompetenciaInicio)}</span>{v.vigenciaCompetenciaFim ? <> até <span className="mono">{formatarCompetencia(v.vigenciaCompetenciaFim)}</span></> : <Pill cor="verde">vigente</Pill>}
                <span className="dim" style={{ fontWeight: 500 }}>{v.fundamentoLegal}</span>
                <span className="acoes">{editavel && <button className="btn mini" type="button" onClick={() => setEditVar({ vi, ii: null })}>+ Variante</button>}</span>
              </div>
              <div className="tbwrap">
                <table className="tb">
                  <thead><tr><th>Aplica-se a</th><th>Regra</th><th>Calendários</th><th>Prazo interno</th><th>Abre</th><th>Prior.</th>{editavel && <th />}</tr></thead>
                  <tbody>
                    {v.variantes.map((va, ii) => (
                      <tr key={va.id || ii}>
                        <td>{va.descricao ? <b>{va.descricao}</b> : <span className="dim">padrão</span>}<div className="dim">{[va.ufs.length ? `UF ${va.ufs.join('/')}` : '', va.municipiosIbge.length ? `mun. ${va.municipiosIbge.join('/')}` : '', va.regimes.length ? va.regimes.join('/') : '', va.anexosSimples.length ? `anexo ${va.anexosSimples.join('/')}` : ''].filter(Boolean).join(' · ') || 'qualquer cliente'}</div></td>
                        <td>{resumoVariante(va)}{va.parcelas.length > 0 && <div className="dim">{va.parcelas.length} quotas</div>}</td>
                        <td className="dim">{va.calendarios.join(', ')}</td>
                        <td className="dim">{va.antecedenciaInternaDiasUteis} d.u. antes</td>
                        <td className="dim">{va.aberturaDiasAntes} dias antes</td>
                        <td className="c">{va.prioridade}</td>
                        {editavel && <td className="acoes"><button className="btn mini" type="button" onClick={() => setEditVar({ vi, ii })}>Editar</button> <button className="btn mini perigo" type="button" onClick={() => setVariante(vi, ii, null)}>×</button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </Sec>

        <Sec num={3} titulo="Prorrogações oficiais" sub="IN/portaria que muda o vencimento de uma competência específica"
          acoes={podeEditar && <button className="btn mini" type="button" onClick={() => setNovaPror({ comp: '', data: '', fundamento: '' })}>+ Prorrogação</button>}>
          <div className="tbwrap">
            <table className="tb">
              <thead><tr><th>Competência</th><th>Parcela</th><th>UFs</th><th>Novo vencimento</th><th>Fundamento</th><th /></tr></thead>
              <tbody>
                {o.prorrogacoes.length === 0 && <tr><td className="vazio" colSpan={6}>nenhuma</td></tr>}
                {o.prorrogacoes.map((p) => (
                  <tr key={p.id}>
                    <td className="mono">{p.competenciaChave ? formatarCompetencia(p.competenciaChave) : 'todas'}</td>
                    <td className="c">{p.parcelaNumero ?? '—'}</td>
                    <td className="dim">{p.ufs.join(', ') || 'todas'}</td>
                    <td className="mono"><b>{fmtData(p.novoVencimento)}</b></td>
                    <td>{p.fundamentoLegal}</td>
                    <td className="acoes">{podeEditar && <button className="btn mini perigo" type="button" onClick={() => removerPror.mutate(p.id)}>Remover</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sec>

        <ChecklistModeloEditor obrigacaoId={o.id} podeEditar={podeEditar} />
      </div>

      {editVar && f.regra && (
        <EditorVariante
          inicial={editVar.ii === null ? null : f.regra.versoes[editVar.vi]!.variantes[editVar.ii]!}
          onFechar={() => setEditVar(null)}
          onSalvar={(va) => { setVariante(editVar.vi, editVar.ii, va); setEditVar(null); }}
        />
      )}
      <Modal titulo="Nova versão da regra" aberto={!!novaVersao} onFechar={() => setNovaVersao(null)} estreito
        acoes={<><button className="btn" type="button" onClick={() => setNovaVersao(null)}>Cancelar</button>
          <button className="btn primario" type="button" disabled={!novaVersao || !lerCompetencia(novaVersao.inicio)} onClick={() => {
            const inicio = lerCompetencia(novaVersao!.inicio)!;
            const fimAnterior = inicio % 100 === 1 ? (Math.floor(inicio / 100) - 1) * 100 + 12 : inicio - 1;
            const atual = f.regra?.versoes.find((v) => v.vigenciaCompetenciaFim == null);
            const versoes = (f.regra?.versoes ?? []).map((v) => (v === atual ? { ...v, vigenciaCompetenciaFim: fimAnterior } : v));
            versoes.unshift({ id: '', vigenciaCompetenciaInicio: inicio, vigenciaCompetenciaFim: null, fundamentoLegal: novaVersao!.fundamento || null, variantes: (atual?.variantes ?? []).map((va) => ({ ...va, id: '', versaoId: '' })) });
            setRegra({ versoes }); setNovaVersao(null);
          }}>Criar</button></>}>
        {novaVersao && (
          <div className="fgrid">
            <CampoCompetencia className="c5" rotulo="Vale a partir da competência" valor={novaVersao.inicio} onChange={(v) => setNovaVersao({ ...novaVersao, inicio: v })} placeholder="mm/aaaa" ajuda="a versão atual é encerrada na competência anterior; as variantes são copiadas para você ajustar" />
            <Campo className="c7" rotulo="Fundamento legal" valor={novaVersao.fundamento} onChange={(v) => setNovaVersao({ ...novaVersao, fundamento: v })} />
          </div>
        )}
      </Modal>
      <Modal titulo="Prorrogação oficial" aberto={!!novaPror} onFechar={() => setNovaPror(null)} estreito
        acoes={<><button className="btn" type="button" onClick={() => setNovaPror(null)}>Cancelar</button><button className="btn primario" type="button" disabled={!novaPror?.data || !novaPror.fundamento || prorrogar.isPending} onClick={() => prorrogar.mutate()}>Registrar</button></>}>
        {novaPror && (
          <div className="fgrid">
            <CampoCompetencia className="c4" rotulo="Competência" valor={novaPror.comp} onChange={(v) => setNovaPror({ ...novaPror, comp: v })} placeholder="mm/aaaa" ajuda="vazio = todas" />
            <Campo className="c4" rotulo="Novo vencimento" valor={novaPror.data} onChange={(v) => setNovaPror({ ...novaPror, data: v })} type="date" obrigatorio />
            <Campo className="c12" rotulo="Fundamento (IN, portaria…)" valor={novaPror.fundamento} onChange={(v) => setNovaPror({ ...novaPror, fundamento: v })} obrigatorio />
          </div>
        )}
      </Modal>
      {duplicar && <ModalDuplicar origem={o} onFechar={() => setDuplicar(false)} />}
    </>
  );
}

export type { RegimeTributario };
